import { Suspense, useState, useRef, useCallback, useEffect } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows, useGLTF, Html } from '@react-three/drei';
import * as THREE from 'three';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import './Configurator.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CarConfig {
  paintColor: string;
  metalness: number;
  roughness: number;
  clearcoat: number;
  wheelColor: string;
  environment: string;
  bgColor: string;
}

interface CameraPreset {
  name: string;
  position: [number, number, number];
  target: [number, number, number];
}

const CAMERA_PRESETS: CameraPreset[] = [
  { name: '3/4 Front', position: [4, 2, 4], target: [0, 0, 0] },
  { name: 'Front', position: [0, 1.5, 5], target: [0, 0, 0] },
  { name: 'Side', position: [6, 1.5, 0], target: [0, 0, 0] },
  { name: 'Rear', position: [0, 1.5, -5], target: [0, 0, 0] },
  { name: 'Top', position: [0, 7, 0.1], target: [0, 0, 0] },
  { name: 'Low', position: [5, 0.5, 3], target: [0, 0.5, 0] },
];

const PAINT_COLORS = [
  { name: 'Obsidian Black', hex: '#1a1a1a' },
  { name: 'Arctic White', hex: '#f0f0f0' },
  { name: 'Racing Red', hex: '#cc0000' },
  { name: 'Deep Blue', hex: '#0a2463' },
  { name: 'British Racing Green', hex: '#004225' },
  { name: 'Midnight Purple', hex: '#2d1b69' },
  { name: 'Sunset Orange', hex: '#e65c00' },
  { name: 'Gunmetal Grey', hex: '#555566' },
  { name: 'Electric Blue', hex: '#0066ff' },
  { name: 'Champagne Gold', hex: '#c9a84c' },
  { name: 'Nardo Grey', hex: '#8a8d8f' },
  { name: 'Miami Blue', hex: '#00b0f0' },
];

const WHEEL_COLORS = [
  { name: 'Silver', hex: '#c0c0c0' },
  { name: 'Gunmetal', hex: '#444450' },
  { name: 'Black', hex: '#1a1a1a' },
  { name: 'Bronze', hex: '#8B6914' },
  { name: 'Gold', hex: '#d4a017' },
  { name: 'White', hex: '#e8e8e8' },
];

const ENVIRONMENTS = [
  'city', 'sunset', 'dawn', 'night', 'warehouse', 'forest',
  'apartment', 'studio', 'park', 'lobby',
];

const BG_COLORS = [
  { name: 'Dark', hex: '#0f0f1a' },
  { name: 'Charcoal', hex: '#1e1e2e' },
  { name: 'Slate', hex: '#2d3748' },
  { name: 'Studio White', hex: '#e8e8ec' },
  { name: 'Warm Grey', hex: '#3a3a3a' },
  { name: 'Deep Navy', hex: '#0a1628' },
];

const DEFAULT_CONFIG: CarConfig = {
  paintColor: '#cc0000',
  metalness: 0.7,
  roughness: 0.25,
  clearcoat: 1.0,
  wheelColor: '#444450',
  environment: 'city',
  bgColor: '#0f0f1a',
};

// ─── 3D Car Model ────────────────────────────────────────────────────────────

function ConfigurableCar({ config }: { config: CarConfig }) {
  const { scene } = useGLTF('/models/sedan-body.glb');
  const clonedScene = useRef<THREE.Group | null>(null);

  // Clone scene once to avoid shared state across renders
  if (!clonedScene.current) {
    clonedScene.current = scene.clone(true);
  }
  const model = clonedScene.current;

  useEffect(() => {
    if (!model) return;

    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 3 / maxDim;

    // Center horizontally, place bottom on ground plane (y=-1.2)
    const minY = box.min.y * scale;
    model.position.set(-center.x * scale, -1.2 - minY, -center.z * scale);
    model.scale.setScalar(scale);
  }, [model]);

  useEffect(() => {
    if (!model) return;

    const paintColor = new THREE.Color(config.paintColor);

    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        // Create a MeshPhysicalMaterial for clearcoat support
        const newMat = new THREE.MeshPhysicalMaterial({
          color: paintColor,
          metalness: config.metalness,
          roughness: config.roughness,
          clearcoat: config.clearcoat,
          clearcoatRoughness: 0.1,
        });

        child.material = newMat;
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [model, config.paintColor, config.metalness, config.roughness, config.clearcoat, config.wheelColor]);

  return (
    <group>
      <primitive object={model} />
    </group>
  );
}

function LoadingSpinner() {
  return (
    <Html center>
      <div style={{
        color: '#8b8bff',
        fontSize: '16px',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
        padding: '20px',
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(139, 139, 255, 0.3)',
          borderTop: '3px solid #8b8bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 12px',
        }} />
        Loading model...
      </div>
    </Html>
  );
}

// ─── Config Panel ────────────────────────────────────────────────────────────

function ConfigPanel({
  config,
  onChange,
}: {
  config: CarConfig;
  onChange: (updates: Partial<CarConfig>) => void;
}) {
  const [openSection, setOpenSection] = useState<string>('paint');

  const toggle = (section: string) =>
    setOpenSection((prev) => (prev === section ? '' : section));

  return (
    <div className="config-panel">
      <div className="config-panel-header">
        <h2>Customize</h2>
      </div>

      <div className="config-sections">
        {/* Paint Color */}
        <Section title="Paint Color" id="paint" open={openSection === 'paint'} onToggle={toggle}>
          <div className="color-grid">
            {PAINT_COLORS.map((c) => (
              <button
                key={c.hex}
                className={`color-swatch ${config.paintColor === c.hex ? 'active' : ''}`}
                style={{ backgroundColor: c.hex }}
                onClick={() => onChange({ paintColor: c.hex })}
                title={c.name}
              />
            ))}
          </div>
          <label className="custom-color-row">
            <span>Custom</span>
            <input
              type="color"
              value={config.paintColor}
              onChange={(e) => onChange({ paintColor: e.target.value })}
            />
          </label>
        </Section>

        {/* Finish */}
        <Section title="Finish" id="finish" open={openSection === 'finish'} onToggle={toggle}>
          <SliderRow
            label="Metallic"
            value={config.metalness}
            onChange={(v) => onChange({ metalness: v })}
          />
          <SliderRow
            label="Roughness"
            value={config.roughness}
            onChange={(v) => onChange({ roughness: v })}
          />
          <SliderRow
            label="Clearcoat"
            value={config.clearcoat}
            onChange={(v) => onChange({ clearcoat: v })}
          />
        </Section>

        {/* Wheels */}
        <Section title="Wheels" id="wheels" open={openSection === 'wheels'} onToggle={toggle}>
          <div className="color-grid">
            {WHEEL_COLORS.map((c) => (
              <button
                key={c.hex}
                className={`color-swatch ${config.wheelColor === c.hex ? 'active' : ''}`}
                style={{ backgroundColor: c.hex }}
                onClick={() => onChange({ wheelColor: c.hex })}
                title={c.name}
              />
            ))}
          </div>
        </Section>

        {/* Environment */}
        <Section title="Environment" id="env" open={openSection === 'env'} onToggle={toggle}>
          <div className="env-grid">
            {ENVIRONMENTS.map((env) => (
              <button
                key={env}
                className={`env-button ${config.environment === env ? 'active' : ''}`}
                onClick={() => onChange({ environment: env })}
              >
                {env}
              </button>
            ))}
          </div>
        </Section>

        {/* Background */}
        <Section title="Background" id="bg" open={openSection === 'bg'} onToggle={toggle}>
          <div className="color-grid">
            {BG_COLORS.map((c) => (
              <button
                key={c.hex}
                className={`color-swatch ${config.bgColor === c.hex ? 'active' : ''}`}
                style={{ backgroundColor: c.hex, border: c.hex === '#e8e8ec' ? '2px solid #ccc' : undefined }}
                onClick={() => onChange({ bgColor: c.hex })}
                title={c.name}
              />
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({
  title,
  id,
  open,
  onToggle,
  children,
}: {
  title: string;
  id: string;
  open: boolean;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className={`config-section ${open ? 'open' : ''}`}>
      <button className="section-header" onClick={() => onToggle(id)}>
        <span>{title}</span>
        <span className="section-chevron">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && <div className="section-body">{children}</div>}
    </div>
  );
}

function SliderRow({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  return (
    <div className="slider-row">
      <div className="slider-label">
        <span>{label}</span>
        <span className="slider-value">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
    </div>
  );
}

// ─── Main Configurator Page ──────────────────────────────────────────────────

export default function Configurator({ onBack }: { onBack: () => void }) {
  const [config, setConfig] = useState<CarConfig>(DEFAULT_CONFIG);
  const controlsRef = useRef<OrbitControlsImpl>(null);

  const updateConfig = useCallback((updates: Partial<CarConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const applyPreset = useCallback((preset: CameraPreset) => {
    const controls = controlsRef.current;
    if (!controls) return;

    controls.object.position.set(...preset.position);
    controls.target.set(...preset.target);
    controls.update();
  }, []);

  return (
    <div className="configurator">
      {/* Top bar */}
      <div className="configurator-topbar">
        <button className="back-button" onClick={onBack}>
          ← Parts Viewer
        </button>
        <h1 className="configurator-title">3D Car Configurator</h1>
      </div>

      <div className="configurator-body">
        {/* 3D Viewport */}
        <div className="configurator-viewport">
          <Canvas
            camera={{ position: [4, 2, 4], fov: 45 }}
            shadows
            gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.2 }}
            style={{ width: '100%', height: '100%', background: config.bgColor }}
          >
            <ambientLight intensity={0.5} />
            <directionalLight
              position={[5, 8, 5]}
              intensity={1.5}
              castShadow
              shadow-mapSize={[2048, 2048]}
              shadow-bias={-0.0001}
            />
            <directionalLight position={[-5, 3, -5]} intensity={0.4} />
            <pointLight position={[0, 5, 0]} intensity={0.3} />

            <Suspense fallback={<LoadingSpinner />}>
              <ConfigurableCar config={config} />
            </Suspense>

            <Suspense fallback={null}>
              <Environment preset={config.environment as any} />
            </Suspense>

            <ContactShadows
              position={[0, -1.2, 0]}
              opacity={0.4}
              scale={12}
              blur={2.5}
              far={5}
            />

            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.201, 0]} receiveShadow>
              <planeGeometry args={[50, 50]} />
              <meshStandardMaterial
                color={config.bgColor}
                metalness={0.1}
                roughness={0.8}
              />
            </mesh>

            <OrbitControls
              ref={controlsRef}
              enablePan={false}
              enableZoom={true}
              enableRotate={true}
              minDistance={3}
              maxDistance={12}
              maxPolarAngle={Math.PI / 2 - 0.05}
              autoRotate
              autoRotateSpeed={0.5}
            />
          </Canvas>

          {/* Camera presets */}
          <div className="camera-presets">
            {CAMERA_PRESETS.map((preset) => (
              <button
                key={preset.name}
                className="camera-preset-btn"
                onClick={() => applyPreset(preset)}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>

        {/* Config sidebar */}
        <ConfigPanel config={config} onChange={updateConfig} />
      </div>
    </div>
  );
}
